import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

const TEXT = {
  es: {
    title: 'API para tu web propia',
    description: 'Si vendes en una web hecha a medida, tu programador puede enviar cada pedido a la app autom\u00e1ticamente con una clave API.',
    docs: 'Ver la documentaci\u00f3n para tu programador',
    namePlaceholder: 'Nombre de la clave (ej. Mi web)',
    create: 'Crear clave',
    creating: 'Creando...',
    newKeyTitle: 'Tu nueva clave API',
    newKeyWarning: 'C\u00f3piala ahora y gu\u00e1rdala en un lugar seguro: no se volver\u00e1 a mostrar. \u00dasala solo en tu servidor, nunca en el navegador.',
    copy: 'Copiar',
    copied: 'Copiada',
    done: 'Ya la he guardado',
    none: 'Todav\u00eda no tienes claves API.',
    created: 'Creada',
    lastUsed: '\u00daltimo uso',
    never: 'nunca',
    revoke: 'Revocar',
    revokeConfirm: '\u00bfRevocar la clave "{{name}}"? Las peticiones que la usen dejar\u00e1n de funcionar al momento.',
    error: 'No se pudo completar la acci\u00f3n.',
  },
  en: {
    title: 'API for your own website',
    description: 'If you sell on a custom-built website, your developer can send every order to the app automatically with an API key.',
    docs: 'See the documentation for your developer',
    namePlaceholder: 'Key name (e.g. My website)',
    create: 'Create key',
    creating: 'Creating...',
    newKeyTitle: 'Your new API key',
    newKeyWarning: 'Copy it now and keep it safe: it will not be shown again. Use it only on your server, never in the browser.',
    copy: 'Copy',
    copied: 'Copied',
    done: 'I have saved it',
    none: 'You have no API keys yet.',
    created: 'Created',
    lastUsed: 'Last used',
    never: 'never',
    revoke: 'Revoke',
    revokeConfirm: 'Revoke the key "{{name}}"? Requests using it will stop working immediately.',
    error: 'The action could not be completed.',
  },
  fr: {
    title: 'API pour votre propre site',
    description: 'Si vous vendez sur un site sur mesure, votre d\u00e9veloppeur peut envoyer chaque commande \u00e0 l\u2019application automatiquement avec une cl\u00e9 API.',
    docs: 'Voir la documentation pour votre d\u00e9veloppeur',
    namePlaceholder: 'Nom de la cl\u00e9 (ex. Mon site)',
    create: 'Cr\u00e9er une cl\u00e9',
    creating: 'Cr\u00e9ation...',
    newKeyTitle: 'Votre nouvelle cl\u00e9 API',
    newKeyWarning: 'Copiez-la maintenant et gardez-la en lieu s\u00fbr : elle ne sera plus affich\u00e9e. Utilisez-la uniquement sur votre serveur, jamais dans le navigateur.',
    copy: 'Copier',
    copied: 'Copi\u00e9e',
    done: 'Je l\u2019ai enregistr\u00e9e',
    none: 'Vous n\u2019avez pas encore de cl\u00e9 API.',
    created: 'Cr\u00e9\u00e9e',
    lastUsed: 'Derni\u00e8re utilisation',
    never: 'jamais',
    revoke: 'R\u00e9voquer',
    revokeConfirm: 'R\u00e9voquer la cl\u00e9 \u00ab {{name}} \u00bb ? Les requ\u00eates qui l\u2019utilisent cesseront de fonctionner imm\u00e9diatement.',
    error: 'L\u2019action n\u2019a pas pu \u00eatre effectu\u00e9e.',
  },
};

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

function ApiKeysCard({ token, onAuthError }) {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleError = (err) => {
    if (err.response && err.response.status === 401) {
      onAuthError();
      return;
    }
    const detail = err.response && err.response.data && err.response.data.detail;
    setError(typeof detail === 'string' ? detail : tx.error);
  };

  const load = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/api-keys`, authHeaders);
      setKeys(response.data.data || []);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy('create');
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/v1/api-keys`, { name }, authHeaders);
      setNewKey(response.data.data);
      setCopied(false);
      setName('');
      load();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy('');
    }
  };

  const revoke = async (key) => {
    if (!window.confirm(tx.revokeConfirm.replace('{{name}}', key.name))) return;
    setBusy(key.id);
    setError('');
    try {
      await axios.delete(`${API_URL}/api/v1/api-keys/${key.id}`, authHeaders);
      setKeys((prev) => prev.filter((k) => k.id !== key.id));
    } catch (err) {
      handleError(err);
    } finally {
      setBusy('');
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
    } catch (err) {
      setCopied(false);
    }
  };

  const formatDate = (value) =>
    value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value)) : tx.never;

  return (
    <div className="bg-surface border border-edge rounded-2xl p-5 sm:p-6 max-w-lg mb-6">
      <p className="text-white text-sm font-semibold mb-1">{tx.title}</p>
      <p className="text-muted text-xs mb-2">{tx.description}</p>
      <a href="/developers" target="_blank" rel="noopener noreferrer" className="text-accent text-xs underline block mb-4">
        {tx.docs}
      </a>

      {newKey && (
        <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-3 mb-4">
          <p className="text-amber-400 text-sm font-semibold mb-1">{tx.newKeyTitle}</p>
          <p className="text-amber-400 text-xs mb-2">{tx.newKeyWarning}</p>
          <code className="block bg-surfaceAlt border border-edge rounded px-2 py-2 text-white text-xs break-all mb-2">{newKey.key}</code>
          <div className="flex gap-2">
            <button type="button" onClick={copyKey} className="bg-accent text-panel font-bold text-xs px-3 py-1.5 rounded cursor-pointer">
              {copied ? tx.copied : tx.copy}
            </button>
            <button type="button" onClick={() => setNewKey(null)} className="bg-transparent border border-edge text-white text-xs px-3 py-1.5 rounded cursor-pointer">
              {tx.done}
            </button>
          </div>
        </div>
      )}

      {keys.length === 0 ? (
        <p className="text-muted text-xs mb-4">{tx.none}</p>
      ) : (
        <div className="flex flex-col gap-2 mb-4">
          {keys.map((key) => (
            <div key={key.id} className="flex items-center justify-between gap-3 bg-surfaceAlt border border-edge rounded-lg p-3">
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{key.name}</p>
                <p className="text-muted text-[11px] font-mono">{key.key_prefix}...</p>
                <p className="text-muted text-[11px]">
                  {tx.created}: {formatDate(key.created_at)}{' \u00b7 '}{tx.lastUsed}: {formatDate(key.last_used_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => revoke(key)}
                disabled={busy === key.id}
                className="text-red-400 text-xs px-3 py-1.5 rounded border border-[#7f1d1d] cursor-pointer shrink-0"
              >
                {tx.revoke}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          placeholder={tx.namePlaceholder}
          className="flex-1 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
        />
        <button
          type="button"
          onClick={create}
          disabled={!!busy || !name.trim()}
          className="bg-accent text-panel font-bold text-sm px-5 py-2 rounded-lg cursor-pointer"
        >
          {busy === 'create' ? tx.creating : tx.create}
        </button>
      </div>
      {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
    </div>
  );
}

export default ApiKeysCard;
